import type { Interpolation, Theme } from '@emotion/react';
import { css } from '@emotion/react';
import type React from 'react';
import type { ButtonProps, GlobalToken } from 'antd';
import type { AriaButtonProps } from 'react-aria';
import { useMemo } from 'react';
import type { IPropsWithTestId } from '../../../constants/data-element-tracking-ids';
import { lightenColor } from '../../../utils/colors/lighten-color';
import getTextColorForBackground from '../../../utils/colors/get-text-color-for-background';
import { theme, type ThemeColor } from '../../styles/theme';
import { getTypographyLinkStyles } from '../Typography';

export enum ButtonVariant {
  PRIMARY = 'primary',
  SECONDARY = 'secondary',
  TERTIARY = 'tertiary',
  GHOST = 'ghost',
  FLAT = 'flat',
}

/** Use the "block" property for a mobile breakpoint */
type IButtonPropsFromAnt =
  // We assign our visual type instead of 'type'
  // Pick<AriaButtonProps, 'isDisabled'> instead of 'disabled'
  // color, variant, shape, ghost, danger - we control ourselves
  Omit<
  ButtonProps,
  | 'type'
  | 'disabled'
  | 'color'
  | 'variant'
  | 'shape'
  | 'ghost'
  | 'danger'
  | 'href'
  | 'htmlType'
  >;

type IExtraButtonPropsFromAnt = Partial<
Pick<ButtonProps, 'type' | 'color' | 'variant' | 'ghost'>
>;

export type IButtonProps = IPropsWithTestId &
IButtonPropsFromAnt &
Pick<AriaButtonProps, 'isDisabled'> & {
  /** CE button variants */
  variant: ButtonVariant;
  /** At the moment, color is only supported for PRIMARY and FLAT buttons. */
  color?: ThemeColor;
  /** Without paddings and with line height */
  inlineButton?: boolean;
  /** For FLAT mainly: hide text decorations */
  hideDecorations?: boolean;
  /** Use the theme token extracted under the nearest ConfigProvider to use expected colors etc. */
  themeToken: GlobalToken;
  /** HTML type */
  type: NonNullable<React.ButtonHTMLAttributes<HTMLButtonElement>['type']>;
  additionalStyles?: Interpolation<Theme>;
};

// ant's type + button props used (internally) when type is not used
const ButtonVisualPropsByVariant: Record<
ButtonVariant,
IExtraButtonPropsFromAnt
> = {
  [ButtonVariant.PRIMARY]: {
    type: 'primary',
    color: 'primary',
    variant: 'solid',
  },
  [ButtonVariant.SECONDARY]: {
    type: 'default',
    variant: 'outlined',
  },
  [ButtonVariant.TERTIARY]: {
    type: 'link',
    variant: 'link',
  },
  [ButtonVariant.GHOST]: {
    type: 'default',
    ghost: true,
    color: 'default',
    variant: 'outlined',
  },
  [ButtonVariant.FLAT]: {
    type: 'link',
    variant: 'link',
  },
};

const getCssForButtonTextColor = (color: string) => `
  color: ${color} !important;
  -webkit-text-fill-color: ${color} !important;
  > .ant-btn-icon > svg {
    color: ${color} !important;
    -webkit-text-fill-color: ${color} !important;
  }
`;

export const GLOBAL_BUTTON_STYLES = css`
  font-family: 'Barlow', sans-serif;
  cursor: pointer;
  font-weight: bold;
  font-size: 16px;
  height: 40px;
  box-shadow: none;
`;

export const useGetButtonVisualProps = ({
  variant,
  themeToken,
  isDisabled,
  loading,
  color,
  inlineButton,
  hideDecorations,
}: Pick<
IButtonProps,
| 'variant'
| 'themeToken'
| 'isDisabled'
| 'loading'
| 'inlineButton'
| 'hideDecorations'
> & {
  color?: ThemeColor;
}) => {
  const disabled = isDisabled || !!loading;
  const primaryColor = color ?? themeToken.colorPrimary;
  const primaryColorText = color
    ? getTextColorForBackground(primaryColor, themeToken)
    : undefined;

  const styles: Interpolation<Theme> = useMemo(
    () => ({
      [ButtonVariant.PRIMARY]: disabled
        ? ''
        : `
      ${
      color
        ? `
            ${
      primaryColorText ? getCssForButtonTextColor(primaryColorText) : ''
      }
            background-color: ${primaryColor} !important;
            &:active {
              background-color: ${lightenColor(primaryColor, -40)} !important;
            };
            `
        : ''
      }
      @media (hover: hover) {
        &:hover {
          background-color: ${lightenColor(primaryColor, 16)} !important;
        };
      };
    `,
      [ButtonVariant.SECONDARY]: '',
      [ButtonVariant.TERTIARY]: disabled
        ? ''
        : `
      ${getCssForButtonTextColor(themeToken.colorPrimary)}
      @media (hover: hover) {
        &:hover {
          ${getCssForButtonTextColor(themeToken.colorPrimaryHover)}
        };
      };
      &:active {
        ${getCssForButtonTextColor(themeToken.colorPrimaryActive)}
      };
    `,
      [ButtonVariant.GHOST]: disabled
        ? `
      border-color: #424242 !important;
      ${getCssForButtonTextColor('#FFFFFF40')}
    `
        : `
    @media (hover: hover) {
      &:hover {
        border-color: ${'#FFFFFF73'} !important;
        ${getCssForButtonTextColor(
        themeToken.Button?.defaultHoverColor ?? '#FFFFFFA6',
      )}
      };
    };
    &:active {
      border-color: #424242 !important;
      background-color: #FFFFFF2E !important;
      ${getCssForButtonTextColor('#FFFFFF')}
    };
  `,
      [ButtonVariant.FLAT]: disabled
        ? ''
        : // TODO: default color later = '#000000' + opacity 90%
      // Now default color is a secondary color to match normal text colors
        getTypographyLinkStyles(
          themeToken,
          !!hideDecorations,
          color ?? theme.colors.secondary,
        ),
    }),
    [
      color,
      disabled,
      hideDecorations,
      themeToken,
      primaryColorText,
      primaryColor,
    ],
  );

  return useMemo(
    () => ({
      styles: css`
        ${GLOBAL_BUTTON_STYLES}
        ${inlineButton
        ? `
        padding: 0;
        height: fit-content;
        justify-content: flex-start;
        line-height: 24px;
        `
        : ''}
        ${styles[variant]}
      `,
      disabled,
      ...ButtonVisualPropsByVariant[variant],
    }),
    [variant, inlineButton, styles, disabled],
  );
};
